import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FileSearchOutlined, CreditCardOutlined, RedoOutlined, PlusCircleOutlined, LikeOutlined, DislikeOutlined, TeamOutlined, FilePptOutlined } from '@ant-design/icons';
import IconButton from '../controls/IconButton/IconButton.jsx';

/**
 * Provides custom action buttons for Ing projects table (detail, archive, restore, apply).
 * Edit and Delete buttons are handled by DataGrid component.
 */
export const IngProjectsTableSettings = ({
  project,
  isArchived = false,
  isWaiting = false,
  isAdmin = false,
  isStudent = false,
  canApply = true,
  applyDisabledTooltip,
  onDetailClick,
  onStudentsClick,
  onArchiveClick,
  onActivateClick,
  canActivateClick,
  onApplyClick,
  onApproveClick,
  onRejectClick,
  onPanelStoryClick,
  panelStoryDisabled,
  panelStoryDisabledTooltip,
}) => {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex' }}>
      {onDetailClick && (
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Detail')}
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(project);
          }}
        />
      )}
      {onStudentsClick && (
        <IconButton
          icon={<TeamOutlined />}
          tooltip={t('Študenti/Záujemcovia')}
          onClick={(e) => {
            e.stopPropagation();
            onStudentsClick(project);
          }}
        />
      )}
      {onPanelStoryClick && !isArchived && !isStudent && (
        <IconButton
          icon={<FilePptOutlined />}
          tooltip={t('Panel Story')}
          disabled={panelStoryDisabled?.(project)}
          disabledTooltip={panelStoryDisabledTooltip}
          onClick={(e) => {
            e.stopPropagation();
            onPanelStoryClick(project);
          }}
        />
      )}
      {isWaiting && onApproveClick && (
        <IconButton
          icon={<LikeOutlined />}
          tooltip={isAdmin ? t('Schváliť projekt okamžite') : t('Schváliť projekt')}
          onClick={(e) => {
            e.stopPropagation();
            onApproveClick(project);
          }}
        />
      )}
      {isWaiting && onRejectClick && (
        <IconButton
          icon={<DislikeOutlined />}
          tooltip={isAdmin ? t('Neschváliť projekt okamžite') : t('Neschváliť projekt')}
          onClick={(e) => {
            e.stopPropagation();
            onRejectClick(project);
          }}
        />
      )}
      {!isArchived && isStudent && (
        <IconButton
          icon={<PlusCircleOutlined />}
          tooltip={t('Prihlásiť sa')}
          disabled={!canApply}
          disabledTooltip={applyDisabledTooltip}
          onClick={(e) => {
            e.stopPropagation();
            onApplyClick?.(project);
          }}
        />
      )}
      {!isArchived && onArchiveClick && (
        <IconButton
          icon={<CreditCardOutlined />}
          tooltip={t('Archivovať')}
          onClick={(e) => {
            e.stopPropagation();
            onArchiveClick(project);
          }}
        />
      )}
      {isArchived && onActivateClick && (!canActivateClick || canActivateClick(project)) && (
        <IconButton
          icon={<RedoOutlined />}
          tooltip={t('Obnoviť')}
          onClick={(e) => {
            e.stopPropagation();
            onActivateClick(project);
          }}
        />
      )}
    </div>
  );
};

IngProjectsTableSettings.propTypes = {
  project: PropTypes.object.isRequired,
  isArchived: PropTypes.bool,
  isWaiting: PropTypes.bool,
  isAdmin: PropTypes.bool,
  isStudent: PropTypes.bool,
  canApply: PropTypes.bool,
  applyDisabledTooltip: PropTypes.string,
  onDetailClick: PropTypes.func,
  onStudentsClick: PropTypes.func,
  onArchiveClick: PropTypes.func,
  onActivateClick: PropTypes.func,
  canActivateClick: PropTypes.func,
  onApplyClick: PropTypes.func,
  onApproveClick: PropTypes.func,
  onRejectClick: PropTypes.func,
  onPanelStoryClick: PropTypes.func,
  panelStoryDisabled: PropTypes.func,
  panelStoryDisabledTooltip: PropTypes.string,
};

export default IngProjectsTableSettings;
